// src/models/Billing.ts
export enum PlanType {
  FREE = "free",
  STARTER = "starter",
  PROFESSIONAL = "professional",
  BUSINESS = "business",
  ENTERPRISE = "enterprise"
}


export const PlanPriceTypeMap: Record<string, string> = {
  // Stripe Test mode (localhost)
  'price_1S6D0JPqDVZWi8yvAYOhcEjX': PlanType.FREE,
  'price_1S6Cv4PqDVZWi8yvEaLuUwFs': PlanType.STARTER,
  'price_1S6CvJPqDVZWi8yvvOfPnJUc': PlanType.PROFESSIONAL,
  'price_1S6CvYPqDVZWi8yvyX3jjB9I': PlanType.BUSINESS,
};

export const planHierarchy = [
  // Stripe Test mode (localhost)
  'price_1S6D0JPqDVZWi8yvAYOhcEjX', // Free
  'price_1S6Cv4PqDVZWi8yvEaLuUwFs',  // Starter
  'price_1S6CvJPqDVZWi8yvvOfPnJUc',  // Professional
  'price_1S6CvYPqDVZWi8yvyX3jjB9I'   // Business
];


export enum SubscriptionStatus {
  ACTIVE = "active",
  CANCELED = "canceled",
  PAST_DUE = "past_due",
  INCOMPLETE = "incomplete"
}

export enum InvoiceStatus {
  DRAFT = "draft",
  OPEN = "open",
  PAID = "paid",
  UNCOLLECTIBLE = "uncollectible",
  VOID = "void"
}

// =================== Plan Configuration ===================

export interface PlanConfig {
  name: string;
  monthly_credits: number;
  price_eur: number; // Price in cents
  stripe_price_id: string;
  features: string[];
}

export interface PlanFeature {
  name: string;
  description: string;
  included: boolean;
}

export interface PlanDetails {
  plan_type: PlanType;
  name: string;
  price_display: string; // "€99/month"
  credits_display: string; // "10,000 credits/month"
  features: PlanFeature[];
  most_popular: boolean;
  stripe_price_id: string;
  is_current: boolean;
}

export interface PricingPageData {
  plans: PlanDetails[];
  features_comparison: Record<string, boolean[]>; // Feature name -> [starter, pro, business]
  credit_price: string; // "€20 per 1,000 credits"
  currency: string;
}

// =================== Subscription Schemas ===================

export interface SubscriptionBase {
  stripe_subscription_id: string;
  stripe_price_id: string;
  status: SubscriptionStatus;
  current_period_start: string; // ISO date string
  current_period_end: string; // ISO date string
  cancel_at_period_end: boolean;
}

export interface SubscriptionResponse extends SubscriptionBase {
  id: string;
  organization_id: string;
  canceled_at?: string; // ISO date string
  created_at: string; // ISO date string
  updated_at: string; // ISO date string

  // NEW: Scheduled plan change fields
  scheduled_plan_change?: string | null;  // Price ID of the plan to change to
  scheduled_change_date?: string | null;  // When the change will happen (ISO datetime)
  stripe_schedule_id?: string | null;
}

// =================== Billing Profile ===================

export interface BillingProfileBase {
  billing_email: string;
  company_name?: string;
  tax_id?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export interface BillingProfileResponse extends BillingProfileBase {
  id: string;
  organization_id: string;
  stripe_customer_id: string;
  created_at: string;
  updated_at: string;
}

export interface BillingProfileUpdate {
  billing_email?: string;
  company_name?: string;
  tax_id?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

// =================== Invoices ===================

export interface InvoiceBase {
  stripe_invoice_id: string;
  invoice_number?: string;
  status: InvoiceStatus;
  amount_due: number;
  amount_paid: number;
  currency: string;
  description?: string;
  invoice_pdf_url?: string;
  period_start?: string; // ISO date string
  period_end?: string; // ISO date string
  due_date?: string; // ISO date string
  paid_at?: string; // ISO date string
}

export interface InvoiceResponse extends InvoiceBase {
  id: string;
  organization_id: string;
  subscription_id?: string;
  created_at: string;
  updated_at: string;
}

// =================== Payment Methods ===================

export interface PaymentMethodBase {
  stripe_payment_method_id: string;
  type: string;
  brand?: string;
  last4?: string;
  exp_month?: string;
  exp_year?: string;
  is_default: boolean;
}

export interface PaymentMethodResponse extends PaymentMethodBase {
  id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export interface PaymentMethodValidation {
  has_payment_method: boolean;
  is_expired: boolean;
  needs_update: boolean;
  brand?: string;
  last4?: string;
}

// =================== API Request/Response ===================

export interface CreateCheckoutSessionRequest {
  price_id: string;
}

export interface CreateCheckoutSessionResponse {
  session_id: string;
  url: string;
}

export interface CreateCreditsCheckoutRequest {
  quantity: number; // Must be >= 1000 and multiple of 1000
}

export interface CustomerPortalResponse {
  url: string;
}

export interface PlanChangeRequest {
  new_price_id: string;
}

export interface CancelSubscriptionRequest {
  cancel_immediately?: boolean;
  cancellation_reason?: string;
}

export interface ResubscribeRequest {
  price_id: string;
  force_checkout?: boolean; 
}

export interface ResubscribeResponse {
  success: boolean;
  requires_payment_method: boolean;
  subscription_id?: string;
  checkout_url?: string;
  session_id?: string;
  message: string;
}

// =================== Usage and Analytics ===================

export interface UsageSummary {
  organization_id: string;
  current_period_start: string; // ISO date string
  current_period_end: string; // ISO date string
  monthly_credits: number;
  remaining_monthly_credits: number;
  extra_credits: number;
  total_credits_used: number;
  usage_percentage: number;
  projected_overage: number;
}

export interface UsageBreakdown {
  chat_credits: number;
  workflow_credits: number;
  api_credits: number;
  total_credits: number;
}

export interface DailyUsage {
  date: string; // ISO date string
  credits_used: number;
  breakdown: UsageBreakdown;
}

export interface UsageAnalytics {
  summary: UsageSummary;
  daily_usage: DailyUsage[];
  usage_trend: 'increasing' | 'decreasing' | 'stable';
  days_until_renewal: number;
}

export interface AlertThreshold {
  percentage: number; // 50-100
  enabled: boolean;
}

export interface UsageAlert {
  type: 'threshold' | 'overage' | 'renewal';
  message: string;
  percentage_used: number;
  credits_remaining: number;
  recommended_action: string;
}

// =================== Proration and Pricing ===================

/**
 * Enhanced Proration Preview with comprehensive VAT/tax information
 * Used to show users exactly what they'll be charged when changing plans
 */
export interface ProrationPreview {
  /** Human-readable description of the proration */
  description: string;
  /** Total proration amount in cents (for backward compatibility) */
  proration_amount: number;
  /** Subtotal before tax in cents */
  subtotal_amount: number;
  /** Tax/VAT amount in cents */
  tax_amount?: number;
  /** Tax percentage (e.g., 20 for 20% VAT) */
  tax_percent?: number;
  /** Total amount including tax in cents */
  total_amount: number;
  /** Credit amount from old plan in cents (if applicable) */
  credit_amount?: number;
  /** Currency code (e.g., 'eur', 'usd') */
  currency: string;
  /** Breakdown of line items */
  line_items?: ProrationLineItem[];
  /** Customer's tax information if available */
  customer_tax?: CustomerTaxInfo;
}

/**
 * Individual line item in proration breakdown
 */
export interface ProrationLineItem {
  description: string;
  amount: number; // in cents
  period_start?: string; // ISO date string
  period_end?: string; // ISO date string
}

/**
 * Customer tax/VAT information
 */
export interface CustomerTaxInfo {
  tax_id?: string;
  tax_id_type?: string; // e.g., 'eu_vat', 'us_ein'
  tax_exempt?: 'none' | 'exempt' | 'reverse';
}

export interface PricingInfo {
  plan_type: PlanType;
  config: PlanConfig;
  is_current_plan: boolean;
}

// =================== Dashboard ===================

export interface BillingDashboard {
  subscription?: SubscriptionResponse;
  billing_profile?: BillingProfileResponse;
  usage_summary: UsageSummary;
  recent_invoices: InvoiceResponse[];
  payment_methods: PaymentMethodResponse[];
  alerts: UsageAlert[];
  available_plans: PricingInfo[];
}

export interface SubscriptionSummary {
  has_active_subscription: boolean;
  current_plan_type?: PlanType;
  subscription_status?: SubscriptionStatus;
  next_billing_date?: string; // ISO date string
  cancel_at_period_end: boolean;
  total_credits_available: number;
  credits_used_this_period: number;
  usage_percentage: number;
}

// =================== Error Types ===================

export interface BillingError {
  error_code: string;
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: Record<string, any>;
}

export interface PaymentFailedError extends BillingError {
  error_code: 'payment_failed';
  retry_instructions: string;
  payment_method_issue: boolean;
}

export interface SubscriptionError extends BillingError {
  error_code: 'subscription_error';
  subscription_id?: string;
}

// =================== Constants ===================

